
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { resolveZustaendig, matchtZustaendigkeitsFilter, ZustaendigTyp } from '../lib/rechnungen/zustaendigkeit';
import { supabase } from '../lib/supabase/client';
import { alleSeiten } from '../lib/supabase/alleSeiten';
import { ExportColumn } from '../lib/export/types';
import { exportCsv } from '../lib/export/exportCsv';
import { exportXlsx } from '../lib/export/exportXlsx';
import { exportPdfReport } from '../lib/export/exportPdf';
import { useIslandActions } from './island/IslandProvider';
import { usePillenSteuerung } from './island/widgets/SteuerungsAnsicht';
import { RechnungsExportAssistent, type RechnungsExportWahl } from './rechnungen/RechnungsExportAssistent';
import { DownloadSimple } from '@phosphor-icons/react';
import { belegDateien, sammelBetreff, teileNachQuelle } from '../lib/rechnungen/sammelBelege';
import { ladeDateiBlob } from '../lib/dokumente/aktenschrankApi';
import EmailVerfassen from '../pages/admin/emails/EmailVerfassen';
import RechnungDetailsSidebar from './RechnungDetailsSidebar';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BestaetigenDialog } from './immobilien/ui/BestaetigenDialog';
import { useAppMeldungen } from './ui/AppMeldungen';
import { GlasTooltipSchicht } from './ui/GlasTooltip';
import {
  filterSichtbareRechnungen, getRichtung, RICHTUNG_LABEL, istMahnung, istMahnungOhneRechnung, istGuthaben, ADMIN_ROLLEN,
} from '../lib/rechnungen/sichtbarkeit';
import { matchObjektByAdresse, ObjektForMatch } from '../lib/rechnungen/objektMatch';
import { klaerungDefaultIso } from '../lib/rechnungen/klaerung';
import { wartetAufKorrektur } from '../lib/rechnungen/korrektur';
import { OHNE_OBJEKT, OHNE_HAUS, effektiveObjektId, passtSuche, summe, gruppiereNachObjekt, gruppiereNachHaus, passtHausFilter, type ObjektGruppe } from '../lib/rechnungen/objektFilter';
import { hausAdresse, sortiereHaeuser, type Haus } from '../lib/objekte/haeuser';
import { useViewport } from './island/hooks';
import { useMobilModus } from '../lib/immobilien/mobil';
import { aktiveFilterChips, ansichtFuerBreite, kartenKopf, kartenMeta, kennzahlen, zustaendigFilterName, type FilterChipKey } from '../lib/rechnungenAnsicht/darstellung';
import {
  AktiveFilterLeiste, FilterFenster, GruppenKopf, GruppierenKnopf, HausAuswahl, KennzahlenWidgets, ListenFehler, ListenLaden, ListenLeer,
  ObjektAuswahl, RechnungenKopf, RechnungsKarte, RichtungWahl, SeitenLeiste, SuchFeld,
} from '../pages/admin/rechnungen/liste/ListenTeile';
import { RechnungsTabelle } from '../pages/admin/rechnungen/liste/Tabelle';
import { Kontextmenue, ObjektSumme, Sammelleiste } from '../pages/admin/rechnungen/liste/Sammelleiste';
import { MAIL_FORM_META } from './island/form/mailMeta';
import { FormRahmen } from './island/form/FormRahmen';

export interface Rechnung {
  id: string;
  rechnungsnummer: string;
  unternehmen: string;
  bezeichnung: string;
  datum: string;
  netto?: number;
  brutto: number;
  steuer?: number;
  steuersatz?: string;
  waehrung: string;
  status: string;
  datei_url?: string;
  zahlungsart?: string;
  iban?: string;
  verwendungszweck?: string;
  zahlungsempfaenger?: string;
  faelligkeitsdatum?: string;
  ist_lastschrift?: boolean;
  skonto_prozent?: number | null;
  skonto_bis?: string | null;
  objekt_id?: string;
  einheit_id?: string | null;
  haus_id?: string | null;
  archiviert_am?: string | null;
  korrektur_angefordert_am?: string | null;
  korrektur_grund?: string | null;
  korrigiert_durch_id?: string | null;
  objekt_adresse?: string;
  objektnummer?: string;
  eigentuemer_id?: string;
  weitergeleitet_am?: string | null;
  klaerung_wiedervorlage_am?: string | null;
  created_at: string;
  is_unklar_source?: boolean;
  bezahlt_timestamp?: string;
  notizen?: string;
  richtung?: 'eingang' | 'ausgang' | null;
  mahnstufe?: number | null;
  letzte_mahnung_am?: string | null;
  mahnung_url?: string | null;
  // Nur im Prüf-Pool: Rechnung, zu der diese Mahnung gehört (Ticket #421).
  zugeordnete_rechnung_id?: string | null;
  dokument_typ?: string | null;
  // Phase A: Display-only Auto-Match (nicht in DB persistiert)
  _matchedObjektnummer?: string;
  _matchedObjektId?: string;
  _isAutoMatched?: boolean;
  zustaendig_id?: string | null;
  zustaendig_typ?: ZustaendigTyp;
  _isTeam?: boolean; // Display-only: true, wenn zuständig ein Team ist
  // Technische Prüfung
  tech_pruefung_von?: string | null;
  tech_pruefung_an?: string | null;
  tech_pruefung_status?: string | null;
  technisch_geprueft_von?: string | null;
  technisch_geprueft_am?: string | null;
  // Vier-Augen-Freigabe
  freigabe_angefordert_von?: string | null;
  freigabe_angefordert_an?: string | null;
  freigegeben?: boolean;
  freigegeben_von?: string | null;
  freigegeben_am?: string | null;
  // Bezahlung
  bezahlt_von?: string | null;
  profiles?: { full_name: string } | null;
  objekte?: { objektnummer: string; objekttyp?: string } | null;
}

interface TeamMember {
  id: string;
  full_name: string;
}

interface Team {
  id: string;
  name: string;
}

interface ObjektSuggestion {
  id: string;
  bezeichnung: string;
  strasse: string;
  hausnummer: string;
}

interface InvoicesViewProps {
  userProfile?: any;
  onNavigateToObjekt?: (id: string) => void;
}

const eurFmt = (v: any) => v != null && v !== '' ? `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : '';
const deDatumFmt = (v: any) => v ? new Date(v).toLocaleDateString('de-DE') : '';

// Extrahiert die Google-Drive-Datei-ID aus datei_url (null bei Nicht-Drive-URLs, z. B. SharePoint).
const getDriveFileId = (url: string | null | undefined): string | null => {
  if (!url) return null;
  const m = url.match(/\/file\/d\/([^/]+)/);
  return m ? m[1] : null;
};

const RECHNUNGEN_EXPORT_COLUMNS: ExportColumn[] = [
  { key: 'rechnungsnummer', label: 'Belegnummer' },
  { key: 'unternehmen', label: 'Aussteller' },
  { key: 'bezeichnung', label: 'Bezeichnung' },
  { key: 'objekt', label: 'Objekt', format: (_v, row) => row.objektnummer || row._matchedObjektnummer || row.objekt_adresse || '' },
  { key: 'richtung', label: 'Richtung', format: (_v, row) => RICHTUNG_LABEL[getRichtung(row)] },
  { key: 'typ', label: 'Typ', format: (_v, row) => istGuthaben(row) ? 'Guthaben' : istMahnungOhneRechnung(row) ? 'Mahnung ohne Rechnung' : istMahnung(row) ? 'Mahnung' : 'Rechnung' },
  { key: 'netto', label: 'Netto', format: eurFmt },
  { key: 'steuer', label: 'Steuer', format: eurFmt },
  { key: 'brutto', label: 'Brutto', format: eurFmt },
  { key: 'datum', label: 'Datum', format: deDatumFmt },
  { key: 'faelligkeitsdatum', label: 'Fälligkeit', format: deDatumFmt },
  { key: 'status', label: 'Status' },
  { key: 'iban', label: 'IBAN' },
  { key: 'verwendungszweck', label: 'Verwendungszweck' },
  { key: 'notizen', label: 'Notizen' },
  { key: 'bezahlt_timestamp', label: 'Bezahlt am', format: deDatumFmt },
  { key: 'profiles', label: 'Bezahlt von', format: (_v, row) => row.profiles?.full_name || '' },
];

// Kompakte Spaltenauswahl NUR für den PDF-Bericht — alle 17 Spalten überlappen im PDF.
const RECHNUNGEN_PDF_COLUMNS: ExportColumn[] = [
  { key: 'rechnungsnummer', label: 'Beleg' },
  { key: 'unternehmen', label: 'Aussteller' },
  { key: 'objekt', label: 'Objekt', format: (_v, row) => row.objektnummer || row._matchedObjektnummer || row.objekt_adresse || '' },
  { key: 'richtung', label: 'Richtung', format: (_v, row) => RICHTUNG_LABEL[getRichtung(row)] },
  { key: 'netto', label: 'Netto', format: eurFmt },
  { key: 'brutto', label: 'Brutto', format: eurFmt },
  { key: 'faelligkeitsdatum', label: 'Fällig', format: deDatumFmt },
  { key: 'status', label: 'Status' },
];

const InvoicesView: React.FC<InvoicesViewProps> = ({ userProfile, onNavigateToObjekt }) => {
  const navigate = useNavigate();
  const { id: routeRechnungId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rechnungen, setRechnungen] = useState<Rechnung[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Spalten-Sortierung
  const [sortFeld, setSortFeld] = useState<'datum' | 'betrag' | 'faelligkeit'>('datum');
  const [sortRichtung, setSortRichtung] = useState<'asc' | 'desc'>('desc');

  // Export-Funnel (in der Insel-Pille): erst 'Exportieren'-Button, dann Schritt 0..4 = Fragen, 5 = Zusammenfassung

  // Rechtsklick-Kontextmenü auf einer Zeile (Öffnen / Löschen)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; row: Rechnung } | null>(null);

  // Inline ausgeklappte Zeile mit Dokument-Vorschau (iframe)
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  // Manuelles Aktualisieren (Button-Feedback ohne die Liste auszublenden)
  const [refreshing, setRefreshing] = useState(false);
  const { notify, openForm, closeForm } = useIslandActions();
  const viewport = useViewport();
  const { mobil } = useMobilModus();
  // Fehler als App-Hinweis statt alert (Freigabe 17.10.2026, Texte unverändert)
  const { melde, dialoge } = useAppMeldungen(mobil);
  // Auf Touch-Geräten Auswahlkästchen mit 44-px-Tippfläche
  const touch = useMemo(() => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches, []);

  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setContextMenu(null); };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [contextMenu]);

  // Objekt-Filter States
  const [selectedObjekt, setSelectedObjekt] = useState<ObjektSuggestion | null>(null);
  // Alle Objekte für Filter-Panel + Gruppen-Labels — kommt aus fetchRechnungen,
  // KEINE eigene Query (die Liste wird dort ohnehin für den Auto-Match geladen).
  const [alleObjekte, setAlleObjekte] = useState<ObjektForMatch[]>([]);
  const [objektFilterOffen, setObjektFilterOffen] = useState(false);
  const [objektFilterSuche, setObjektFilterSuche] = useState('');
  const objektFilterRef = useRef<HTMLDivElement>(null);
  const [gruppiereObjekt, setGruppiereObjekt] = useState(false);
  // Optionale Haus-Ebene: alle Häuser aller Liegenschaften (winzige Tabelle);
  // leer = keine Liegenschaft hat Häuser, dann bleibt die Liste wie bisher.
  const [alleHaeuser, setAlleHaeuser] = useState<Haus[]>([]);
  const [selectedHausId, setSelectedHausId] = useState<string | null>(null);
  const [hausFilterOffen, setHausFilterOffen] = useState(false);
  const hausFilterRef = useRef<HTMLDivElement>(null);
  const [offeneGruppen, setOffeneGruppen] = useState<Set<string>>(new Set());

  const [stats, setStats] = useState({
    offeneSum: 0,
    offeneCount: 0,
    overdueCount: 0,
    unklarCount: 0,
    offeneSumAusgang: 0, // Forderungen (eigene Ausgangsrechnungen)
    offeneSumEingang: 0, // Verbindlichkeiten (Eingangsrechnungen)
  });
  
  const [selectedInvoice, setSelectedInvoice] = useState<Rechnung | null>(null);
  const [deletingInvoice, setDeletingInvoice] = useState<Rechnung | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Filter States
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('alle');
  const [filterRichtung, setFilterRichtung] = useState<'alle' | 'eingang' | 'ausgang'>('alle');
  const [filterAssignee, setFilterAssignee] = useState<string | 'alle' | 'global'>(
    userProfile?.role === 'team' ? (userProfile?.id || 'alle') : 'alle'
  );
  // Zusätzliche Filter: Mahnung/Guthaben (Typ) + Zeitraum auf `datum`
  const [filterMahnungOnly, setFilterMahnungOnly] = useState(false);
  const [filterKorrekturOnly, setFilterKorrekturOnly] = useState(false);
  const [filterGuthabenOnly, setFilterGuthabenOnly] = useState(false);
  const [filterSepaSkontoOnly, setFilterSepaSkontoOnly] = useState(false);
  const [filterDatumVon, setFilterDatumVon] = useState('');
  const [filterDatumBis, setFilterDatumBis] = useState('');

  // Sammelaktionen (Multi-Select) — nur echte `rechnungen`, KI-Pool (is_unklar_source) ausgeschlossen
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAssigneeOpen, setBulkAssigneeOpen] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const [openColumnFilter, setOpenColumnFilter] = useState<string | null>(null);
  const [openAssigneeId, setOpenAssigneeId] = useState<string | null>(null);
  const [openStatusId, setOpenStatusId] = useState<string | null>(null);
  const [updatingAssigneeId, setUpdatingAssigneeId] = useState<string | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);

  const filterRef = useRef<HTMLDivElement>(null);
  const assigneeRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const bulkAssigneeRef = useRef<HTMLDivElement>(null);
  // Merker fuer den Zeilen-Klick: Begann der Mousedown auf einem Knopf in der Zeile,
  // oeffnet der Klick NICHT die Rechnung. Die Zeile skaliert beim Ueberfahren
  // (hover:scale) — dabei rutscht das Ziel unter dem Zeiger weg, das click-Ereignis
  // feuert am gemeinsamen Vorfahren, und stopPropagation am Knopf kommt zu spaet
  // (dieselbe Falle wie in EmailListe.tsx:219).
  const klickStartAufKnopfRef = useRef(false);

  useEffect(() => {
    fetchData();
  }, []);

  // URL ist die Wahrheitsquelle fuer das offene Detail: selectedInvoice wird aus dem
  // Route-Param :id abgeleitet — oeffnen, schliessen, Refresh-Sync und ungueltige id.
  useEffect(() => {
    if (!routeRechnungId) { setSelectedInvoice(null); return; }
    if (rechnungen.length === 0) return; // auf Laden warten
    const target = rechnungen.find(r => String(r.id) === String(routeRechnungId));
    if (target) {
      setSelectedInvoice(target);
    } else {
      navigate('/admin/rechnungen', { replace: true });
    }
  }, [routeRechnungId, rechnungen]);

  // Deep-Link von der Objektseite: /admin/rechnungen?objekt=<id|ohne>
  // Parameter wird nach dem Anwenden entfernt, damit „Filter zurücksetzen"
  // nicht beim nächsten Render wieder überschrieben wird.
  useEffect(() => {
    const wunsch = searchParams.get('objekt');
    if (!wunsch) return;
    if (wunsch === 'ohne') {
      setSelectedObjekt({ id: OHNE_OBJEKT, bezeichnung: 'Ohne Objekt', strasse: '', hausnummer: '' });
    } else {
      if (alleObjekte.length === 0) return; // Objektliste noch nicht da → später erneut
      const o = alleObjekte.find((x) => x.id === wunsch);
      if (o) {
        setSelectedObjekt({ id: o.id, bezeichnung: o.bezeichnung || o.objektnummer || 'Objekt', strasse: o.strasse || '', hausnummer: o.hausnummer || '' });
      }
    }
    setCurrentPage(1);
    const rest = new URLSearchParams(searchParams);
    rest.delete('objekt');
    setSearchParams(rest, { replace: true });
  }, [searchParams, alleObjekte, setSearchParams]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Spaltenfilter nur bei Klick außerhalb schließen — vorher schloss JEDER Mausdruck ihn,
      // auch der auf eine Option, sodass die Auswahl nie ankam.
      if (!(event.target as HTMLElement).closest?.('[data-spaltenfilter]')) setOpenColumnFilter(null);
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilterMenu(false);
      }
      if (assigneeRef.current && !assigneeRef.current.contains(event.target as Node)) {
        setOpenAssigneeId(null);
      }
      if (statusRef.current && !statusRef.current.contains(event.target as Node)) {
        setOpenStatusId(null);
      }
      if (bulkAssigneeRef.current && !bulkAssigneeRef.current.contains(event.target as Node)) {
        setBulkAssigneeOpen(false);
      }
      if (objektFilterRef.current && !objektFilterRef.current.contains(event.target as Node)) {
        setObjektFilterOffen(false);
      }
      if (hausFilterRef.current && !hausFilterRef.current.contains(event.target as Node)) {
        setHausFilterOffen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Esc schließt jedes offene Aufklappmenü der Liste (Handoff §6/E5) — nicht, solange ein Dialog darüber liegt.
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || document.querySelector('[aria-modal="true"]')) return;
      setShowFilterMenu(false); setObjektFilterOffen(false); setHausFilterOffen(false); setOpenColumnFilter(null);
      setOpenAssigneeId(null); setOpenStatusId(null); setBulkAssigneeOpen(false);
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    // Mitarbeiter + Teams zuerst laden, damit fetchRechnungen die Namen sofort auflösen kann.
    const [members, teamsData] = await Promise.all([loadTeamMembers(), loadTeams()]);
    setTeamMembers(members);
    setTeams(teamsData);
    await fetchRechnungen(members, teamsData);
    setLoading(false);
  };

  const loadTeamMembers = async (): Promise<TeamMember[]> => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('id, full_name')
        .or('role.eq.team,role.eq.admin').eq('zuweisbar', true)
        .not('email', 'ilike', '%labelyou%');
      return data || [];
    } catch (err) {
      console.error("Error fetching team members", err);
      return [];
    }
  };

  const loadTeams = async (): Promise<Team[]> => {
    try {
      const { data } = await supabase.from('teams').select('id, name').order('name');
      return data || [];
    } catch (err) {
      console.error("Error fetching teams", err);
      return [];
    }
  };

  const fetchRechnungen = async (members: TeamMember[] = teamMembers, teamsData: Team[] = teams) => {
    setError(null);
    try {
      // Wir joinen 'objekte(objektnummer)', um die Nummer aus dem verknüpften Objekt zu erhalten.
      // Zusätzlich laden wir alle Objekte für Phase-A-Auto-Match auf objekt_adresse.
      // Seitenweise laden: Supabase liefert höchstens 1000 Zeilen je Abfrage — vorher fehlten ältere
      // Belege in Liste, Suche und Kennzahlen, und Direktlinks auf sie sprangen zur Liste zurück.
      // Feste Sortierung nach id, damit sich die Seiten nicht überschneiden.
      const [normalDaten, respUnklar, respObjekteAll, respHaeuser] = await Promise.all([
        alleSeiten((von, bis) => supabase.from('rechnungen').select('*, objekte(objektnummer, objekttyp)').order('id').range(von, bis)),
        // Pool wie bisher: ein Fehler hier blendet nur den Pool aus, statt die ganze Liste zu verwerfen.
        alleSeiten((von, bis) => supabase.from('rechnungen_unklar').select('*').order('id').range(von, bis))
          .then((data) => ({ data }), () => ({ data: null })),
        supabase.from('objekte').select('id, objektnummer, bezeichnung, strasse, hausnummer, plz, stadt'),
        supabase.from('objekt_haeuser').select('id, objekt_id, bezeichnung, strasse, hausnummer, plz, stadt, sortierung'),
      ]);
      const respNormal = { data: normalDaten };

      const allObjekte: ObjektForMatch[] = respObjekteAll.data || [];
      setAlleObjekte(allObjekte);
      setAlleHaeuser(sortiereHaeuser((respHaeuser.data as Haus[]) || []));

      const normalList: Rechnung[] = (respNormal.data || []).map(item => {
        const z = resolveZustaendig(item.zustaendig_id, item.zustaendig_typ, members, teamsData);
        const r: Rechnung = {
          ...item,
          objektnummer: item.objekte?.objektnummer || item.objektnummer,
          profiles: z ? { full_name: z.name } : null,
          _isTeam: !!z?.isTeam,
        };
        // Auto-Match nur wenn keine echte Verknüpfung besteht und Adresse vorhanden
        if (!r.objekt_id && r.objekt_adresse) {
          const match = matchObjektByAdresse(r.objekt_adresse, allObjekte);
          if (match) {
            r._matchedObjektnummer = match.objektnummer || '';
            r._matchedObjektId = match.id;
            r._isAutoMatched = true;
          }
        }
        return r;
      });

      const unklarList: Rechnung[] = (respUnklar.data || []).map(item => {
        const zu = resolveZustaendig(item.zustaendig_id, item.zustaendig_typ, members, teamsData);
        const r: Rechnung = {
          ...item,
          rechnungsnummer: 'POOL-' + item.id.toString().slice(0, 4).toUpperCase(),
          unternehmen: item.unternehmen || 'KI-ERKENNUNG LÄUFT',
          bezeichnung: item.bezeichnung || 'Unbekannter Beleg',
          brutto: item.brutto || 0,
          waehrung: 'EUR',
          status: item.status || 'unklar',
          is_unklar_source: true,
          profiles: zu ? { full_name: zu.name } : null,
          _isTeam: !!zu?.isTeam,
          objektnummer: item.objektnummer || '',
          dokument_typ: item.dokument_typ,
        };
        // Auto-Match auch für unklar-Pool
        if (!r.objekt_id && r.objekt_adresse) {
          const match = matchObjektByAdresse(r.objekt_adresse, allObjekte);
          if (match) {
            r._matchedObjektnummer = match.objektnummer || '';
            r._matchedObjektId = match.id;
            r._isAutoMatched = true;
          }
        }
        return r;
      });

      const combinedList = [...unklarList, ...normalList].sort((a, b) => {
        return new Date(b.created_at || b.datum).getTime() - new Date(a.created_at || a.datum).getTime();
      });

      // --- SICHTBARKEITS FILTERUNG (Grundmenge) ---
      const visibleList = filterSichtbareRechnungen(combinedList, {
        id: userProfile?.id || '',
        role: userProfile?.role,
        rechnungen_bereich: userProfile?.rechnungen_bereich,
        team_member_ids: userProfile?.team_member_ids,
        team_ids: userProfile?.team_ids,
        sieht_alle_rechnungen: userProfile?.sieht_alle_rechnungen,
      });

      setRechnungen(visibleList);
      updateStats(visibleList);
      // selectedInvoice wird vom Route-Param-Effekt synchronisiert (haengt an rechnungen).
    } catch (err: any) {
      console.error("Fetch Error:", err);
      setError("Datenbank-Fehler beim Laden der Rechnungen.");
    }
  };

  // Aktualisieren-Button: lädt neu, zeigt Spinner + kurze Bestätigung (Liste bleibt sichtbar).
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await fetchRechnungen();
      notify({ title: 'Rechnungen aktualisiert', tone: 'success' });
    } finally {
      setRefreshing(false);
    }
  };

  // Kennzahlen: dieselbe Rechnung wie bisher, jetzt testbar in lib/rechnungenAnsicht (kennzahlen).
  const updateStats = (fullList: Rechnung[]) => {
    setStats(kennzahlen(fullList));
  };

  const handleDeleteInvoice = async (e: React.MouseEvent, invoice: Rechnung) => {
    e.stopPropagation();
    setDeletingInvoice(invoice);
  };

  const confirmDelete = async () => {
    if (!deletingInvoice) return;
    setIsDeleting(true);

    try {
      const targetTable = deletingInvoice.is_unklar_source ? 'rechnungen_unklar' : 'rechnungen';

      // Timeline zuerst entfernen (FK-Schutz)
      if (!deletingInvoice.is_unklar_source) {
        const { error: timelineErr } = await supabase
          .from('rechnungen_timeline')
          .delete()
          .eq('rechnung_id', deletingInvoice.id);
        if (timelineErr) console.warn('Timeline-Cleanup fehlgeschlagen:', timelineErr);
      }

      const { data: deletedRows, error } = await supabase
        .from(targetTable)
        .delete()
        .eq('id', deletingInvoice.id)
        .select('id');

      if (error) throw error;
      if (!deletedRows || deletedRows.length === 0) {
        throw new Error('Beleg konnte nicht gelöscht werden — vermutlich fehlende Berechtigung (RLS-Policy prüfen).');
      }

      setRechnungen(prev => prev.filter(r => r.id !== deletingInvoice.id));
      updateStats(rechnungen.filter(r => r.id !== deletingInvoice.id));
      setDeletingInvoice(null);
    } catch (err: any) {
      melde('Fehler beim Löschen: ' + (err?.message || 'Unbekannter Fehler'), true);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAssigneeUpdate = async (invoice: Rechnung, newId: string | null, newTyp: 'mitarbeiter' | 'team' | null) => {
    setUpdatingAssigneeId(invoice.id);
    setOpenAssigneeId(null);
    try {
      const targetTable = invoice.is_unklar_source ? 'rechnungen_unklar' : 'rechnungen';
      const { error } = await supabase
        .from(targetTable)
        .update({ zustaendig_id: newId, zustaendig_typ: newTyp })
        .eq('id', invoice.id);

      if (error) throw error;

      const z = resolveZustaendig(newId, newTyp, teamMembers, teams);
      setRechnungen(prev => prev.map(r =>
        r.id === invoice.id ? { ...r, zustaendig_id: newId, zustaendig_typ: newTyp, profiles: z ? { full_name: z.name } : null, _isTeam: !!z?.isTeam } : r
      ));
    } catch (err: any) {
      melde('Fehler bei der Zuweisung: ' + err.message, true);
    } finally {
      setUpdatingAssigneeId(null);
    }
  };

  const handleStatusUpdate = async (invoice: Rechnung, newStatus: string) => {
    setUpdatingStatusId(invoice.id);
    setOpenStatusId(null);
    try {
      const targetTable = invoice.is_unklar_source ? 'rechnungen_unklar' : 'rechnungen';
      const updateData: any = { status: newStatus };

      // bezahlt_timestamp existiert nur in rechnungen, nicht in rechnungen_unklar (KI-Pool)
      if (!invoice.is_unklar_source) {
        updateData.bezahlt_timestamp = newStatus.toLowerCase() === 'bezahlt'
          ? new Date().toISOString()
          : null;
      }

      // Klärungs-Wiedervorlage: beim Setzen auf Klärung Default jetzt+7 Tage,
      // beim Verlassen des Status nullen (Spec 2026-07-28).
      updateData.klaerung_wiedervorlage_am = newStatus.toLowerCase() === 'klaerung'
        ? klaerungDefaultIso(new Date())
        : null;

      const { error } = await supabase
        .from(targetTable)
        .update(updateData)
        .eq('id', invoice.id);

      if (error) throw error;

      setRechnungen(prev => prev.map(r =>
        r.id === invoice.id ? { ...r, ...updateData } : r
      ));
    } catch (err: any) {
      melde('Fehler beim Status-Update: ' + err.message, true);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  // Wiedervorlage-Datum einer Klärungs-Rechnung setzen (Preset in Tagen oder konkretes Datum).
  const setzeKlaerungWiedervorlage = async (invoice: Rechnung, tage: number) => {
    const iso = new Date(Date.now() + tage * 24 * 60 * 60 * 1000).toISOString();
    await schreibeKlaerungWiedervorlage(invoice, iso);
  };
  const setzeKlaerungWiedervorlageDatum = async (invoice: Rechnung, datum: string) => {
    await schreibeKlaerungWiedervorlage(invoice, new Date(`${datum}T09:00:00`).toISOString());
  };
  const schreibeKlaerungWiedervorlage = async (invoice: Rechnung, iso: string) => {
    const targetTable = invoice.is_unklar_source ? 'rechnungen_unklar' : 'rechnungen';
    const { error } = await supabase.from(targetTable).update({ klaerung_wiedervorlage_am: iso }).eq('id', invoice.id);
    if (error) { melde(`Wiedervorlage fehlgeschlagen: ${error.message}`, true); return; }
    setRechnungen(prev => prev.map(r => r.id === invoice.id ? { ...r, klaerung_wiedervorlage_am: iso } : r));
    setOpenStatusId(null);
  };

  // --- SAMMELAKTIONEN (Multi-Select) ---
  // KI-Pool-Zeilen (is_unklar_source) leben in `rechnungen_unklar`, nicht `rechnungen` —
  // für sie sind Sammelaktionen (bezahlt/zuständig) nicht anwendbar.
  const toggleSelectRow = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    // KI-Pool zählt mit: bei der Belegprüfung will man nicht danach sortieren, ob die KI
    // einen Beleg schon einbuchen konnte (User 2026-08-12).
    const selectableIds = sichtbareZeilen.map(r => r.id);
    const alleAusgewaehlt = selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (alleAusgewaehlt) {
        selectableIds.forEach(id => next.delete(id));
      } else {
        selectableIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkMarkPaid = async () => {
    if (selectedIds.size === 0) return;
    setBulkError(null);
    setBulkUpdating(true);
    const ids = Array.from(selectedIds);
    try {
      // klaerung_wiedervorlage_am nullen (Spec 2026-07-28) — Sammelaktion kann auch
      // Klärungs-Rechnungen treffen; bezahlt ist nie klaerung.
      const updateData = { status: 'bezahlt', bezahlt_timestamp: new Date().toISOString(), klaerung_wiedervorlage_am: null };
      // Auswahl kann beide Quellen enthalten. rechnungen_unklar kennt kein bezahlt_timestamp —
      // dort nur den Status setzen.
      const gewaehlt = rechnungen.filter(r => ids.includes(r.id));
      const { rechnungen: echte, pool } = teileNachQuelle(gewaehlt as any[]);
      if (echte.length) {
        const { error } = await supabase.from('rechnungen').update(updateData).in('id', echte.map(r => r.id));
        if (error) throw error;
      }
      if (pool.length) {
        const { error } = await supabase.from('rechnungen_unklar')
          .update({ status: 'bezahlt', klaerung_wiedervorlage_am: null }).in('id', pool.map(r => r.id));
        if (error) throw error;
      }

      const idSet = new Set(ids);
      setRechnungen(prev => prev.map(r => (idSet.has(r.id) ? { ...r, ...updateData } : r)));
      clearSelection();
    } catch (err: any) {
      console.error('Fehler bei Sammel-Status-Update:', err);
      setBulkError('Als bezahlt markieren fehlgeschlagen: ' + (err?.message || 'Unbekannter Fehler'));
    } finally {
      setBulkUpdating(false);
    }
  };

  // Belege der Auswahl herunterladen: die PDFs liegen in Google Drive, deshalb einzeln
  // abrufen und nacheinander in den Download-Ordner geben (kein Massen-Abruf, der die
  // Drive-Function ausbremst). Belege ohne hinterlegte PDF werden am Ende benannt.
  const [bulkFortschritt, setBulkFortschritt] = useState<string | null>(null);
  const handleBulkDownload = async () => {
    const gewaehlt = rechnungen.filter(r => selectedIds.has(r.id));
    if (gewaehlt.length === 0) return;
    const { dateien, ohneDatei } = belegDateien(gewaehlt as any[]);
    if (dateien.length === 0) { setBulkError('Zu den gewählten Belegen liegt keine PDF vor.'); return; }
    setBulkError(null);
    setBulkUpdating(true);
    const gescheitert: string[] = [];
    try {
      for (let i = 0; i < dateien.length; i++) {
        const d = dateien[i];
        setBulkFortschritt(`${i + 1} von ${dateien.length}`);
        try {
          const blob = await ladeDateiBlob(d.fileId, 'application/pdf');
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = d.dateiname;
          document.body.appendChild(a); a.click(); a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 3000);
        } catch { gescheitert.push(d.dateiname); }
      }
      const hinweise = [
        ohneDatei.length ? `${ohneDatei.length} ohne hinterlegte PDF` : '',
        gescheitert.length ? `${gescheitert.length} nicht ladbar` : '',
      ].filter(Boolean);
      if (hinweise.length) setBulkError(`Nicht alles geladen: ${hinweise.join(', ')}.`);
      else notify({ title: `${dateien.length} Belege heruntergeladen`, tone: 'success' });
    } finally {
      setBulkFortschritt(null);
      setBulkUpdating(false);
    }
  };

  // Auswahl per Mail verschicken: EIN Verfassen-Fenster mit allen PDFs als Anhang.
  const handleBulkMail = async () => {
    const gewaehlt = rechnungen.filter(r => selectedIds.has(r.id));
    if (gewaehlt.length === 0) return;
    const { dateien, ohneDatei } = belegDateien(gewaehlt as any[]);
    if (dateien.length === 0) { setBulkError('Zu den gewählten Belegen liegt keine PDF vor.'); return; }
    setBulkError(null);
    const { data: konten } = await supabase
      .from('email_konten').select('id, email_adresse, bezeichnung, absender_name')
      .eq('aktiv', true).order('ist_standard', { ascending: false }).limit(1);
    const konto = konten?.[0];
    if (!konto) { setBulkError('Kein aktives E-Mail-Postfach gefunden.'); return; }
    openForm(
      <EmailVerfassen
        variant="pille"
        konto={{ id: konto.id, email_adresse: konto.email_adresse, bezeichnung: konto.bezeichnung || konto.absender_name || konto.email_adresse }}
        autoAnhaenge={dateien.map(d => ({ fileId: d.fileId, name: d.dateiname, mimeType: 'application/pdf' }))}
        draft={{ an: [], betreff: sammelBetreff(gewaehlt as any[]), text_inhalt: '' }}
        onClose={() => closeForm()}
        onSent={() => { closeForm(); clearSelection(); }}
      />,
      dateien.length === 1 ? 'Beleg senden' : `${dateien.length} Belege senden`,
      640, MAIL_FORM_META,
    );
    if (ohneDatei.length) setBulkError(`${ohneDatei.length} Beleg(e) ohne PDF wurden nicht angehängt.`);
  };

  const handleBulkAssignee = async (newId: string | null, newTyp: 'mitarbeiter' | 'team' | null) => {
    if (selectedIds.size === 0) return;
    setBulkError(null);
    setBulkAssigneeOpen(false);
    setBulkUpdating(true);
    const ids = Array.from(selectedIds);
    try {
      const gewaehlt = rechnungen.filter(r => ids.includes(r.id));
      const { rechnungen: echte, pool } = teileNachQuelle(gewaehlt as any[]);
      for (const [tabelle, teil] of [['rechnungen', echte], ['rechnungen_unklar', pool]] as const) {
        if (!teil.length) continue;
        const { error } = await supabase.from(tabelle)
          .update({ zustaendig_id: newId, zustaendig_typ: newTyp }).in('id', teil.map(r => r.id));
        if (error) throw error;
      }

      const idSet = new Set(ids);
      const z = resolveZustaendig(newId, newTyp, teamMembers, teams);
      setRechnungen(prev => prev.map(r =>
        idSet.has(r.id) ? { ...r, zustaendig_id: newId, zustaendig_typ: newTyp, profiles: z ? { full_name: z.name } : null, _isTeam: !!z?.isTeam } : r
      ));
      clearSelection();
    } catch (err: any) {
      console.error('Fehler bei Sammel-Zuständigkeits-Update:', err);
      setBulkError('Zuständigkeit setzen fehlgeschlagen: ' + (err?.message || 'Unbekannter Fehler'));
    } finally {
      setBulkUpdating(false);
    }
  };

  // --- EXPORT-FUNNEL: baut aus den Funnel-Antworten den Datensatz + exportiert ---
  // Export der Rechnungen (Logik wie bisher) – aufgerufen aus dem Export-Assistenten in der Pille.
  const exportiereRechnungen = async (exp: RechnungsExportWahl): Promise<number> => {
    const heute = new Date(); heute.setHours(0, 0, 0, 0);
    const basis = exp.umfang === 'alle' ? rechnungen.filter(r => !r.archiviert_am) : sortedRechnungen;
    const daten = basis.filter(r => {
      if (exp.richtung !== 'alle' && getRichtung(r) !== exp.richtung) return false;
      const s = (r.status || '').toLowerCase();
      if (exp.status === 'offen' && !(s === 'offen' || s === 'unklar')) return false;
      if (exp.status === 'bezahlt' && s !== 'bezahlt') return false;
      if (exp.status === 'overdue') {
        if (s === 'bezahlt' || s === 'klaerung') return false;
        if (!(r.faelligkeitsdatum && new Date(r.faelligkeitsdatum) < heute)) return false;
      }
      if (exp.zeitraum !== 'alle') {
        if (!r.datum) return false;
        const d = new Date(r.datum);
        if (exp.zeitraum === 'monat' && !(d.getMonth() === heute.getMonth() && d.getFullYear() === heute.getFullYear())) return false;
        if (exp.zeitraum === 'jahr' && d.getFullYear() !== heute.getFullYear()) return false;
      }
      return true;
    });
    const berichtStats = {
      items: [
        { label: 'Gesamt', value: daten.length },
        { label: 'Offen', value: daten.filter(r => ['offen', 'unklar'].includes((r.status || '').toLowerCase())).length },
        { label: 'Bezahlt', value: daten.filter(r => (r.status || '').toLowerCase() === 'bezahlt').length },
        { label: 'Summe Brutto', value: `${daten.reduce((sum, r) => sum + (Number(r.brutto) || 0), 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` },
      ],
    };
    const cfg = { title: 'Rechnungen', data: daten, stats: berichtStats };
    if (exp.format === 'csv') exportCsv({ ...cfg, columns: RECHNUNGEN_EXPORT_COLUMNS });
    else if (exp.format === 'xlsx') exportXlsx({ ...cfg, columns: RECHNUNGEN_EXPORT_COLUMNS });
    else exportPdfReport({ ...cfg, columns: RECHNUNGEN_PDF_COLUMNS });
    return daten.length;
  };

  const clearObjektFilter = () => {
    setSelectedObjekt(null);
    setCurrentPage(1);
  };

  // Häuser der gefilterten Liegenschaft; leer = kein Haus-Filter, keine Haus-Gruppen.
  const haeuserZumFilter = selectedObjekt && selectedObjekt.id !== OHNE_OBJEKT
    ? alleHaeuser.filter((h) => h.objekt_id === selectedObjekt.id)
    : [];

  // Objektwechsel (oder Objektfilter aus) beendet auch den Haus-Filter — sonst
  // bliebe eine unsichtbare Einschränkung stehen und die Liste wäre leer.
  useEffect(() => {
    setSelectedHausId(null);
  }, [selectedObjekt?.id]);

  const hausName = (id: string | null | undefined) =>
    (id ? alleHaeuser.find((h) => h.id === id)?.bezeichnung : '') || '';

  // --- FILTER LOGIK ---
  const filteredRechnungen = rechnungen.filter(r => {
    // 0. Archiv: archivierte Belege überall ausblenden, außer der Filter "Archiviert" ist aktiv
    if (filterStatus === 'archiviert') {
      if (!r.archiviert_am) return false;
    } else if (r.archiviert_am) {
      return false;
    }

    // 1. Objekt-Filter (effektiver Bezug = objekt_id ODER Auto-Match; Sentinel = ohne Objekt).
    //    Läuft ab jetzt ZUSAMMEN mit der Textsuche (vorher schlossen sich beide aus).
    if (selectedObjekt) {
      const eff = effektiveObjektId(r);
      if (selectedObjekt.id === OHNE_OBJEKT ? eff !== null : eff !== selectedObjekt.id) return false;
    }

    // 1b. Haus-Filter — nur aktiv, wenn eine Liegenschaft mit Häusern gefiltert ist.
    if (!passtHausFilter(r, selectedHausId)) return false;

    // 2. Freitextsuche (Beleg- + Objektfelder + Betrag, siehe lib/rechnungen/objektFilter)
    const matchesSearch = passtSuche(r, searchTerm);

    // 3. Status
    let matchesStatus = true;
    if (filterStatus === 'zur_pruefung') {
      matchesStatus = r.tech_pruefung_an === userProfile?.id && r.tech_pruefung_status === 'angefordert';
    } else if (filterStatus === 'zur_freigabe') {
      matchesStatus = r.freigabe_angefordert_an === userProfile?.id && !r.freigegeben;
    } else if (filterStatus === 'overdue') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      matchesStatus = (r.status?.toLowerCase() !== 'bezahlt' && r.status?.toLowerCase() !== 'klaerung') && r.faelligkeitsdatum ? new Date(r.faelligkeitsdatum) < today : false;
    } else if (filterStatus === 'archiviert') {
      // Archiv-Filterung passiert bereits in Schritt 0 (archiviert_am); Originalstatus ignorieren
      matchesStatus = true;
    } else if (filterStatus !== 'alle') {
      matchesStatus = r.status?.toLowerCase() === filterStatus.toLowerCase();
    }

    // 4. Zuständigkeit (Global / Person / Team) — Kodierung siehe lib/rechnungen/zustaendigkeit
    const matchesAssignee = matchtZustaendigkeitsFilter(filterAssignee, r.zustaendig_id, r.zustaendig_typ ?? null);

    // 5. Richtung (NULL gilt als 'eingang')
    let matchesRichtung = true;
    if (filterRichtung === 'ausgang') {
      matchesRichtung = r.richtung === 'ausgang';
    } else if (filterRichtung === 'eingang') {
      matchesRichtung = r.richtung !== 'ausgang';
    }

    // 6. Typ: Nur Mahnungen / Nur Guthaben / Nur SEPA-Lastschrift oder Skonto
    const matchesMahnung = !filterMahnungOnly || istMahnung(r);
    const matchesKorrektur = !filterKorrekturOnly || wartetAufKorrektur(r);
    const matchesGuthaben = !filterGuthabenOnly || istGuthaben(r);
    const matchesSepaSkonto = !filterSepaSkontoOnly || !!r.ist_lastschrift || r.skonto_prozent != null;

    // 7. Zeitraum (auf `datum`)
    let matchesZeitraum = true;
    const rDatum = r.datum ? new Date(r.datum) : null;
    if (filterDatumVon && rDatum) {
      matchesZeitraum = matchesZeitraum && rDatum >= new Date(filterDatumVon);
    }
    if (filterDatumBis && rDatum) {
      const bis = new Date(filterDatumBis);
      bis.setHours(23, 59, 59, 999);
      matchesZeitraum = matchesZeitraum && rDatum <= bis;
    }
    if ((filterDatumVon || filterDatumBis) && !rDatum) {
      matchesZeitraum = false;
    }

    return matchesSearch && matchesStatus && matchesAssignee && matchesRichtung && matchesMahnung && matchesKorrektur && matchesGuthaben && matchesSepaSkonto && matchesZeitraum;
  });

  // --- SORTIERUNG (vor Pagination) ---
  // Default datum/desc entspricht dem bisherigen Verhalten (neueste zuerst).
  const sortedRechnungen = [...filteredRechnungen].sort((a, b) => {
    const dir = sortRichtung === 'asc' ? 1 : -1;
    if (sortFeld === 'betrag') {
      return dir * ((Number(a.brutto) || 0) - (Number(b.brutto) || 0));
    }
    if (sortFeld === 'faelligkeit') {
      // Belege ohne Fälligkeitsdatum landen unabhängig von der Sortierrichtung am Ende.
      const aZeit = a.faelligkeitsdatum ? new Date(a.faelligkeitsdatum).getTime() : null;
      const bZeit = b.faelligkeitsdatum ? new Date(b.faelligkeitsdatum).getTime() : null;
      if (aZeit === null && bZeit === null) return 0;
      if (aZeit === null) return 1;
      if (bZeit === null) return -1;
      return dir * (aZeit - bZeit);
    }
    // datum (Default)
    const aZeit = new Date(a.datum || a.created_at).getTime();
    const bZeit = new Date(b.datum || b.created_at).getTime();
    return dir * (aZeit - bZeit);
  });

  const toggleSort = (feld: 'datum' | 'betrag' | 'faelligkeit') => {
    setCurrentPage(1);
    if (sortFeld === feld) {
      setSortRichtung(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortFeld(feld);
      setSortRichtung('desc');
    }
  };

  // Beide Richtungen der Korrektur-Verknüpfung, ohne zusätzlichen Datenzugriff: gespeichert
  // ist nur korrigiert_durch_id, die Gegenrichtung wird aus derselben (vollständig
  // geladenen) Liste rückwärts gelesen. Zeigt der Partner nicht in die geladene Menge,
  // bleibt der Eintrag ohne Nummer — dann wird nur der Hinweis ohne Sprung angezeigt.
  const korrekturPartner = useMemo(() => {
    const nummerJeId = new Map(rechnungen.map((r) => [r.id, r.rechnungsnummer]));
    const nachfolger = new Map<string, { id: string; nummer?: string }>();
    const vorgaenger = new Map<string, { id: string; nummer?: string }>();
    for (const r of rechnungen) {
      if (!r.korrigiert_durch_id) continue;
      nachfolger.set(r.id, { id: r.korrigiert_durch_id, nummer: nummerJeId.get(r.korrigiert_durch_id) });
      vorgaenger.set(r.korrigiert_durch_id, { id: r.id, nummer: r.rechnungsnummer });
    }
    return { nachfolger, vorgaenger };
  }, [rechnungen]);

  const currentItems = sortedRechnungen.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(sortedRechnungen.length / itemsPerPage);

  // Gruppen-Ansicht: Blöcke je Objekt über die GESAMTE gefilterte Menge
  // (keine Seiten-Blätterung — Belege erscheinen erst beim Aufklappen).
  const objektLabel = (id: string) => {
    const o = alleObjekte.find((x) => x.id === id);
    if (!o) return 'Unbekanntes Objekt';
    return o.objektnummer ? `${o.objektnummer} · ${o.bezeichnung || ''}`.trim() : (o.bezeichnung || 'Objekt');
  };
  // Ist eine Liegenschaft mit Häusern gefiltert, sind Objekt-Gruppen sinnlos
  // (es gäbe nur eine) — dann wird eine Ebene tiefer nach Haus gruppiert.
  const gruppiereNachHausEbene = gruppiereObjekt && haeuserZumFilter.length > 0;
  const gruppen: ObjektGruppe<Rechnung>[] = !gruppiereObjekt
    ? []
    : gruppiereNachHausEbene
      ? gruppiereNachHaus(sortedRechnungen, haeuserZumFilter.map((h) => h.id), (id) => hausName(id) || 'Unbekanntes Haus')
      : gruppiereNachObjekt(sortedRechnungen, objektLabel);

  type AnzeigeEintrag =
    | { typ: 'gruppe'; gruppe: ObjektGruppe<Rechnung> }
    | { typ: 'zeile'; row: Rechnung };

  const anzeigeEintraege: AnzeigeEintrag[] = gruppiereObjekt
    ? gruppen.flatMap((g) => [
        { typ: 'gruppe' as const, gruppe: g },
        ...(offeneGruppen.has(g.key) ? g.rechnungen.map((row) => ({ typ: 'zeile' as const, row })) : []),
      ])
    : currentItems.map((row) => ({ typ: 'zeile' as const, row }));

  // EINE Wahrheit für „sichtbare Zeilen" — im Gruppen-Modus sind das die Belege
  // der aufgeklappten Gruppen, sonst die aktuelle Pagination-Seite. Die Kopf-
  // Checkbox und ihr Zustand MÜSSEN darauf zeigen, sonst würden Sammelaktionen
  // unsichtbare Belege treffen.
  const sichtbareZeilen: Rechnung[] = anzeigeEintraege
    .filter((e): e is { typ: 'zeile'; row: Rechnung } => e.typ === 'zeile')
    .map((e) => e.row);

  const selectableCurrentItems = sichtbareZeilen;
  const allVisibleSelected = selectableCurrentItems.length > 0 && selectableCurrentItems.every(r => selectedIds.has(r.id));

  // Auswahl niemals über einen Filterwechsel hinaus mitschleppen: Belege, die
  // gerade nicht mehr in der gefilterten Menge sind, könnten sonst von einer
  // Sammelaktion getroffen werden, ohne dass der Nutzer sie sehen kann.
  const filteredIdsKey = filteredRechnungen.map((r) => r.id).join(',');
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const erlaubt = new Set(filteredRechnungen.map((r) => r.id));
      const next = new Set([...prev].filter((id) => erlaubt.has(id)));
      return next.size === prev.size ? prev : next;
    });
    // filteredIdsKey ist die stabile Signatur der gefilterten Menge
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredIdsKey]);

  // Ausgewählte Belege, die im aktuellen Blick gerade nicht gerendert sind
  // (zugeklappte Gruppe, andere Seite) — Hinweis in der Sammelleiste.
  const sichtbareIdsSet = new Set(sichtbareZeilen.map((r) => r.id));
  const selectedNichtSichtbarAnzahl = [...selectedIds].filter((id) => !sichtbareIdsSet.has(id)).length;

  const toggleGruppe = (key: string) =>
    setOffeneGruppen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  const resetFilters = () => {
    setFilterStatus('alle');
    setFilterAssignee('alle');
    setSearchTerm('');
    setSelectedObjekt(null);
    setFilterMahnungOnly(false);
    setFilterKorrekturOnly(false);
    setFilterGuthabenOnly(false);
    setFilterSepaSkontoOnly(false);
    setFilterDatumVon('');
    setFilterDatumBis('');
  };

  const isAdmin = ADMIN_ROLLEN.includes((userProfile?.role || '').toLowerCase());
  const hasActiveFilters = filterStatus !== 'alle' || filterAssignee !== 'alle' || searchTerm !== '' || selectedObjekt !== null
    || filterMahnungOnly || filterKorrekturOnly || filterGuthabenOnly || filterSepaSkontoOnly || filterDatumVon !== '' || filterDatumBis !== '';

  // Objektliste im Panel: clientseitig gefiltert (keine DB-Abfrage → keine
  // PostgREST-Filter-Injection), alphabetisch nach Bezeichnung.
  const objektFilterBegriff = objektFilterSuche.trim().toLowerCase();
  const gefilterteObjekte = alleObjekte
    .filter((o) => {
      if (!objektFilterBegriff) return true;
      return [o.objektnummer, o.bezeichnung, o.strasse, o.stadt]
        .some((f) => (f || '').toLowerCase().includes(objektFilterBegriff));
    })
    .sort((a, b) => (a.bezeichnung || '').localeCompare(b.bezeichnung || '', 'de'));

  // Anzahl der Belege ohne jeden Objektbezug (echte Verknüpfung ODER Auto-Match).
  // Archivierte zählen nicht mit — die Liste blendet sie ebenfalls aus (Schritt 0 der Filterlogik).
  const ohneObjektAnzahl = rechnungen.filter((r) => !r.archiviert_am && effektiveObjektId(r) === null).length;

  // Summenzeile über der Liste — rechnet über die GEFILTERTE Menge (Zeitraum/
  // Status/Zuständigkeit wirken also mit); muss NACH filteredRechnungen stehen.
  const objektKennzahlen = summe(filteredRechnungen);

  const fetchAllRechnungen = async () => {
    const { data } = await supabase.from('rechnungen').select('*').order('datum', { ascending: false }).limit(10000);
    return data || [];
  };

  const rechnungenStats = {
    items: [
      { label: 'Gesamt', value: filteredRechnungen.length },
      { label: 'Offen', value: filteredRechnungen.filter((r: any) => r.status === 'offen' || r.status === 'Offen').length },
      { label: 'Bezahlt', value: filteredRechnungen.filter((r: any) => r.status === 'bezahlt' || r.status === 'Bezahlt').length },
      { label: 'Summe Brutto', value: `${filteredRechnungen.reduce((sum: number, r: any) => sum + (Number(r.brutto) || 0), 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` },
    ]
  };

  // Export in der Pille: Widget öffnet den Assistenten (Format → Umfang → Richtung → Status → Zeitraum) als Formular.
  usePillenSteuerung({
    titel: 'Rechnungen',
    eintraege: [
      { id: 'export', typ: 'aktion', farbe: 'gruen', icon: DownloadSimple, kicker: 'Export', titel: 'Exportieren', unter: 'CSV, Excel oder PDF-Bericht',
        tooltip: 'Rechnungen gefiltert als CSV, Excel oder PDF-Bericht exportieren',
        onClick: () => openForm(
          // Neuer Pillen-Kopf, Inhalt des Assistenten unverändert (Entscheidung: nur Rahmen).
          <FormRahmen>
            <RechnungsExportAssistent
              anzahlAnsicht={filteredRechnungen.length}
              onExport={exportiereRechnungen}
              onFertig={(anzahl) => { closeForm(); notify({ title: `Export erstellt · ${anzahl} Belege`, tone: 'success' }); }}
            />
          </FormRahmen>,
          'Rechnungen exportieren',
          480,
          { icon: DownloadSimple, farbe: 'gruen' },
        ) },
    ],
  }, [filteredRechnungen.length, rechnungen, sortedRechnungen]);

  // Ab 1200 px Tabelle, darunter Karten (Handoff §8). Auswahl ist auch in den Karten möglich,
  // sonst gingen die Sammelaktionen zwischen 1024 und 1200 px verloren (bisherige Grenze war lg).
  const zeigeTabelle = ansichtFuerBreite(viewport.width) === 'tabelle';
  const filterChips = aktiveFilterChips({
    objektName: selectedObjekt ? selectedObjekt.bezeichnung : null, status: filterStatus, zustaendig: filterAssignee, suche: searchTerm,
    nurMahnungen: filterMahnungOnly, nurKorrektur: filterKorrekturOnly, nurGuthaben: filterGuthabenOnly, nurSepaSkonto: filterSepaSkontoOnly,
    datumVon: filterDatumVon, datumBis: filterDatumBis,
  }, zustaendigFilterName(filterAssignee, teamMembers, teams));
  const filterChipEntfernen = (k: FilterChipKey) => {
    switch (k) {
      case 'objekt': clearObjektFilter(); break;
      case 'status': setFilterStatus('alle'); break;
      case 'zustaendig': setFilterAssignee('alle'); break;
      case 'suche': setSearchTerm(''); break;
      case 'mahnung': setFilterMahnungOnly(false); break;
      case 'korrektur': setFilterKorrekturOnly(false); break;
      case 'guthaben': setFilterGuthabenOnly(false); break;
      case 'sepa': setFilterSepaSkontoOnly(false); break;
      case 'zeitraum': setFilterDatumVon(''); setFilterDatumBis(''); break;
    }
  };
  const objektWaehlen = (id: string | null) => {
    if (id === null) setSelectedObjekt(null);
    else if (id === OHNE_OBJEKT) setSelectedObjekt({ id: OHNE_OBJEKT, bezeichnung: 'Ohne Objekt', strasse: '', hausnummer: '' });
    else {
      const o = alleObjekte.find((x) => x.id === id);
      if (o) setSelectedObjekt({ id: o.id, bezeichnung: o.bezeichnung || o.objektnummer || 'Objekt', strasse: o.strasse || '', hausnummer: o.hausnummer || '' });
    }
    setObjektFilterOffen(false); setObjektFilterSuche(''); setCurrentPage(1);
  };

  // Detail als eigene Seite (Entscheidung 17.10.2026): dieselbe Komponente bleibt eingehängt,
  // damit Filter, Seite und Sortierung beim Zurück erhalten bleiben.
  if (routeRechnungId) {
    return (
      <>
        {selectedInvoice ? (
          <RechnungDetailsSidebar
            rechnung={selectedInvoice}
            onClose={() => navigate('/admin/rechnungen')}
            onRefresh={fetchRechnungen}
            onObjectClick={onNavigateToObjekt}
            userProfile={userProfile}
          />
        ) : error ? <ListenFehler text={error} onErneut={handleRefresh} laeuft={refreshing} /> : <ListenLaden />}
        {dialoge}
        <GlasTooltipSchicht />
      </>
    );
  }

  return (
    <div className="flex flex-col" style={{ gap: 18 }}>
      <RechnungenKopf isAdmin={isAdmin} aktualisiert={refreshing} gesperrt={refreshing || loading} onAktualisieren={handleRefresh} mobil={mobil} />
      <RichtungWahl wert={filterRichtung} onWahl={(r) => { setFilterRichtung(r); setCurrentPage(1); }} mobil={mobil} />
      <KennzahlenWidgets k={stats} status={filterStatus} onStatus={setFilterStatus} />

      {/* Filterzeile — z-index 30, damit die Aufklappfenster über der Liste liegen. */}
      <div className="relative flex flex-wrap items-center" style={{ zIndex: 30, gap: 8 }}>
        <SuchFeld wert={searchTerm} onWert={setSearchTerm} />
        <ObjektAuswahl containerRef={objektFilterRef} offen={objektFilterOffen} onUmschalten={() => setObjektFilterOffen((v) => !v)}
          label={selectedObjekt ? selectedObjekt.bezeichnung : 'Objekt'} aktiv={!!selectedObjekt}
          suche={objektFilterSuche} onSuche={setObjektFilterSuche}
          objekte={gefilterteObjekte.map((o) => ({ id: o.id, titel: `${o.objektnummer ? `${o.objektnummer} · ` : ''}${o.bezeichnung || '—'}`, unter: `${o.strasse || ''} ${o.hausnummer || ''}`.trim() }))}
          gewaehltId={selectedObjekt?.id ?? null} ohneId={OHNE_OBJEKT} ohneAnzahl={ohneObjektAnzahl} onWahl={objektWaehlen} />
        {haeuserZumFilter.length > 0 && (
          <HausAuswahl containerRef={hausFilterRef} offen={hausFilterOffen} onUmschalten={() => setHausFilterOffen((v) => !v)}
            label={selectedHausId === OHNE_HAUS ? 'Ohne Haus' : (hausName(selectedHausId) || 'Haus')} aktiv={!!selectedHausId}
            haeuser={haeuserZumFilter.map((h) => ({ id: h.id, titel: h.bezeichnung, unter: hausAdresse(h) || '' }))}
            gewaehltId={selectedHausId} ohneId={OHNE_HAUS}
            onWahl={(id) => { setSelectedHausId(id); setHausFilterOffen(false); setCurrentPage(1); }} />
        )}
        {/* Gruppieren — reine Darstellung, ändert weder Filter noch Export. */}
        <GruppierenKnopf an={gruppiereObjekt} nachHaus={haeuserZumFilter.length > 0} onUmschalten={() => { setGruppiereObjekt((v) => !v); setCurrentPage(1); }} />
        <FilterFenster containerRef={filterRef} offen={showFilterMenu} onUmschalten={() => setShowFilterMenu(!showFilterMenu)} onSchliessen={() => setShowFilterMenu(false)}
          aktiv={filterStatus !== 'alle' || filterAssignee !== 'alle'}
          status={filterStatus} onStatus={setFilterStatus} zustaendig={filterAssignee} onZustaendig={setFilterAssignee}
          isAdmin={isAdmin} eigeneId={userProfile?.id || ''} mitglieder={teamMembers} teams={teams}
          nurMahnungen={filterMahnungOnly} onNurMahnungen={() => { setFilterMahnungOnly(v => !v); setCurrentPage(1); }}
          nurKorrektur={filterKorrekturOnly} onNurKorrektur={() => { setFilterKorrekturOnly(v => !v); setCurrentPage(1); }}
          nurGuthaben={filterGuthabenOnly} onNurGuthaben={() => { setFilterGuthabenOnly(v => !v); setCurrentPage(1); }}
          nurSepaSkonto={filterSepaSkontoOnly} onNurSepaSkonto={() => { setFilterSepaSkontoOnly(v => !v); setCurrentPage(1); }}
          von={filterDatumVon} onVon={(v) => { setFilterDatumVon(v); setCurrentPage(1); }}
          bis={filterDatumBis} onBis={(v) => { setFilterDatumBis(v); setCurrentPage(1); }}
          onReset={resetFilters} />
      </div>

      <AktiveFilterLeiste chips={filterChips} onEntfernen={filterChipEntfernen} onAlleLoeschen={resetFilters} />

      {selectedIds.size > 0 && (
        <Sammelleiste anzahl={selectedIds.size} nichtSichtbar={selectedNichtSichtbarAnzahl} laeuft={bulkUpdating} fortschritt={bulkFortschritt} fehler={bulkError}
          onBezahlt={handleBulkMarkPaid} onHerunterladen={handleBulkDownload} onMail={handleBulkMail}
          zustaendigRef={bulkAssigneeRef} zustaendigOffen={bulkAssigneeOpen} onZustaendigUmschalten={() => setBulkAssigneeOpen(o => !o)}
          mitglieder={teamMembers} teams={teams} onZustaendig={handleBulkAssignee} onAufheben={clearSelection} mobil={mobil} />
      )}

      {/* Summenzeile bei gesetztem Objekt-Filter — rechnet über die GEFILTERTE Menge (Zeitraum/Status/Zuständigkeit wirken mit). */}
      {selectedObjekt && (
        <ObjektSumme name={selectedObjekt.bezeichnung} anzahl={objektKennzahlen.anzahl} brutto={objektKennzahlen.brutto} offen={objektKennzahlen.offen} />
      )}

      {loading ? (
        <ListenLaden />
      ) : error ? (
        <ListenFehler text={error} onErneut={handleRefresh} laeuft={refreshing} />
      ) : filteredRechnungen.length === 0 ? (
        <ListenLeer onReset={resetFilters} />
      ) : zeigeTabelle ? (
        <RechnungsTabelle eintraege={anzeigeEintraege} kompakt={false}
          ausgewaehlt={selectedIds} alleSichtbarenGewaehlt={allVisibleSelected} auswahlMoeglich={selectableCurrentItems.length > 0}
          onAuswahl={toggleSelectRow} onAlleSichtbaren={toggleSelectAllVisible}
          sortFeld={sortFeld} sortRichtung={sortRichtung} onSort={toggleSort}
          offenerSpaltenfilter={openColumnFilter} setOffenerSpaltenfilter={setOpenColumnFilter}
          filterZustaendig={filterAssignee} setFilterZustaendig={setFilterAssignee} filterStatus={filterStatus} setFilterStatus={setFilterStatus}
          mitglieder={teamMembers} teams={teams}
          offeneZustaendigkeit={openAssigneeId} setOffeneZustaendigkeit={setOpenAssigneeId} zustaendigRef={assigneeRef}
          aktualisiertZustaendigkeit={updatingAssigneeId} onZustaendigkeit={handleAssigneeUpdate}
          offenerStatus={openStatusId} setOffenerStatus={setOpenStatusId} statusRef={statusRef}
          aktualisiertStatus={updatingStatusId} onStatus={handleStatusUpdate}
          onWiedervorlageTage={setzeKlaerungWiedervorlage} onWiedervorlageDatum={setzeKlaerungWiedervorlageDatum}
          offeneGruppen={offeneGruppen} onGruppe={toggleGruppe} nachHaus={gruppiereNachHausEbene}
          hausName={hausName} partner={korrekturPartner} mitAdresse={!selectedObjekt} markiertId={selectedInvoice?.id ?? null}
          onOeffnen={(id) => { if (klickStartAufKnopfRef.current) { klickStartAufKnopfRef.current = false; return; } navigate('/admin/rechnungen/' + id); }}
          onSprung={(id) => navigate('/admin/rechnungen/' + id)}
          onZeileMausDruck={(e) => { klickStartAufKnopfRef.current = !!(e.target as HTMLElement).closest('button'); }}
          onKontextmenue={(e, row) => { e.preventDefault(); setContextMenu({ x: e.clientX, y: e.clientY, row }); }}
          vorschauId={expandedRowId} onVorschauSchliessen={() => setExpandedRowId(null)} driveId={getDriveFileId} />
      ) : (
        <div className="flex flex-col" style={{ gap: 8 }}>
          {anzeigeEintraege.map((eintrag) => {
            if (eintrag.typ === 'gruppe') {
              const g = eintrag.gruppe;
              return (
                <GruppenKopf key={`gruppe-${g.key}`} label={g.label} anzahl={g.summe.anzahl} brutto={eurFmt(g.summe.brutto)} offen={eurFmt(g.summe.offen)}
                  aufgeklappt={offeneGruppen.has(g.key)} onUmschalten={() => toggleGruppe(g.key)} nachHaus={gruppiereNachHausEbene} />
              );
            }
            const row = eintrag.row;
            return (
              <RechnungsKarte key={row.id} kopf={kartenKopf(row.rechnungsnummer, deDatumFmt(row.datum))} status={row.status} wiedervorlage={row.klaerung_wiedervorlage_am}
                firma={row.unternehmen || '—'} bezeichnung={row.bezeichnung || ''} meta={kartenMeta(row, hausName(row.haus_id))}
                gewaehlt={selectedIds.has(row.id)} onAuswahl={() => toggleSelectRow(row.id)} onOeffnen={() => navigate('/admin/rechnungen/' + row.id)} touch={touch} />
            );
          })}
        </div>
      )}

      {!loading && !error && !gruppiereObjekt && filteredRechnungen.length > itemsPerPage && (
        <SeitenLeiste seite={currentPage} seiten={totalPages} onSeite={setCurrentPage} />
      )}

      {deletingInvoice && (
        <BestaetigenDialog titel="Rechnung löschen" bestaetigenLabel="Löschvorgang bestätigen" laeuft={isDeleting}
          text={`Möchten Sie die Rechnung ${deletingInvoice.rechnungsnummer} von ${deletingInvoice.unternehmen} wirklich unwiderruflich löschen?`}
          onBestaetigen={confirmDelete} onAbbrechen={() => setDeletingInvoice(null)} />
      )}
      {dialoge}
      <GlasTooltipSchicht />

      {/* Rechtsklick-Kontextmenü auf einer Zeile */}
      {contextMenu && (
        <Kontextmenue x={contextMenu.x} y={contextMenu.y}
          onOeffnen={() => { setExpandedRowId(prev => prev === contextMenu.row.id ? null : contextMenu.row.id); setContextMenu(null); }}
          onLoeschen={() => { setDeletingInvoice(contextMenu.row); setContextMenu(null); }} />
      )}
    </div>
  );
};

export default InvoicesView;
